// /////////////////////////////////////////////////////////////
// [ファイル] AddressAutocomplete.tsx
// [概要] 住所1入力欄の freeSolo Autocomplete。入力中に既存建物をサジェストし、
//        「既に登録がある住所・建物」への事前牽制を入力動線そのものに埋め込む。
//        mode='link' (顧客フォーム) は候補選択で住所転記+建物リンク確定、
//        mode='warn' (建物の手動登録) は候補を選択不可の情報表示に留め重複登録を注意喚起する。
//        候補に無い住所はそのまま入力可 (保存時のサーバー側自動解決が最終防衛線)。
// /////////////////////////////////////////////////////////////
import React, { useRef, useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import { ep } from '../api/endpoints';

const SEARCH_BUILDINGS_URL = ep('search_crm_buildings');

// 顧客と建物の住所連動リンク (保存時に building_link として送信)
export interface BuildingLinkValue {
  relation_id: number | null;   // 既存リンクの編集時のみ (null=新規リンク)
  building_id: string;
  building_name: string;
  building_address: string;     // 建物の住所 (住所1へ転記)
  postal_code: string;          // 建物の郵便番号 (郵便番号へ転記)
  room_no: string;
  relation_type: string;        // resident / tenant / owner
}

// search_crm_buildings の候補1件
export interface BuildingSuggestion {
  id: string;
  building_name: string;
  building_name_kana?: string;
  postal_code?: string;
  address_main?: string;
  building_type?: string;
  registration_source?: string;
  relation_count?: number;      // is_current な紐付け顧客数 (事前牽制の表示用)
}

interface AddressAutocompleteProps {
  value: string;                                    // 住所1のテキスト (親の formData と同期)
  mode: 'link' | 'warn';
  onTextChange: (text: string) => void;             // 自由入力時 (親側で建物リンクをクリア)
  onBuildingSelect?: (building: BuildingSuggestion) => void;  // mode='link' の候補選択時
  excludeBuildingId?: string | null;                // mode='warn' 編集中の建物自身を候補から除外
  placeholder?: string;
}

export default function AddressAutocomplete({
  value,
  mode,
  onTextChange,
  onBuildingSelect,
  excludeBuildingId,
  placeholder,
}: AddressAutocompleteProps) {
  const [options, setOptions] = useState<BuildingSuggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // debounce + 連番でレスポンスの新旧を判定 (古い結果の上書き防止)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const handleSearch = (query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) {
      setOptions([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const seq = ++searchSeqRef.current;
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(SEARCH_BUILDINGS_URL, { query });
        if (seq !== searchSeqRef.current) return;
        const results: BuildingSuggestion[] = res.data.status === 'success' ? (res.data.results || []) : [];
        setOptions(excludeBuildingId ? results.filter((b) => b.id !== excludeBuildingId) : results);
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        console.error('[Address Suggest] Error:', err);
        setOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setSearchLoading(false);
      }
    }, 300);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Autocomplete<BuildingSuggestion, false, false, true>
        freeSolo
        options={options}
        size="small"
        fullWidth
        loading={searchLoading}
        filterOptions={(x) => x}
        value={null}
        inputValue={value || ''}
        onInputChange={(_, newInput, reason) => {
          // reason='reset' (候補選択時の内部同期) では親を更新しない。転記は onBuildingSelect 側で行う
          if (reason === 'input' || reason === 'clear') {
            onTextChange(newInput);
            handleSearch(newInput);
          }
        }}
        onChange={(_, newValue) => {
          if (mode === 'link' && newValue && typeof newValue !== 'string') {
            onBuildingSelect?.(newValue);
            setOptions([]);
          }
        }}
        getOptionDisabled={() => mode === 'warn'}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : (option.address_main || option.building_name || '')
        }
        renderOption={(props, option) => (
          <li {...props} key={option.id} style={{ ...(props as any).style, opacity: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Icons.HomeWork sx={{ fontSize: 15, color: '#2e7d32' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.building_name}</Typography>
                {option.building_type && (
                  <Chip label={option.building_type} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                )}
                {option.registration_source === 'customer_auto' && (
                  <Chip label="簡易" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#fff3e0', color: '#e65100' }} />
                )}
                {(option.relation_count ?? 0) > 0 && (
                  <Typography variant="caption" sx={{ color: '#1565c0' }}>
                    紐付け顧客{option.relation_count}名
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.disabled" noWrap>
                {option.address_main || ''}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder || '住所を入力 (登録済みの建物を候補表示)'} />
        )}
      />
      {mode === 'warn' && (value || '').trim() !== '' && options.length > 0 && (
        <Typography variant="caption" sx={{ color: '#ed6c02', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Icons.WarningAmber sx={{ fontSize: 14 }} />
          類似の住所・建物が登録済みです。重複登録にご注意ください
        </Typography>
      )}
    </Box>
  );
}
